import type {SlotsType, VNode} from 'vue';

/** This component conditionally wraps its default slot content in a wrapper slot
 *
 * Usage:
 * ```vue
 * <MaybeWrapped :wrapped="condition">
 *   <template #wrapper="{children}">
 *     <div class="wrapper-class">
 *       <Component :is="children" />
 *     </div>
 *   </template>
 *   <template #default>
 *     <SomeComponent />
 *   </template>
 * </MaybeWrapped>
 * ```
 *
 * Renders either
 * ```html
 * <div class="wrapper-class">
 *   <SomeComponent />
 * </div>
 * ```
 * or just
 * ```html
 * <SomeComponent />
 * ```
 * depending on the value of `wrapped` prop.
 */
export default defineComponent(
	({wrapped}, {slots}) => () => wrapped
		? slots.wrapper?.({
			children: () => slots.default?.(),
			wrapped,
		})
		: slots.default?.(),
	{
		props: {wrapped: Boolean},
		slots: Object as SlotsType<{
			default: void;
			wrapper: {
				children: () => VNode[];
				wrapped: boolean;
			};
		}>,
	},
);
